import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import styles from './Table.scss';

const initialFilters = {
    page: 1,
    sort: 'asc',
    limit: 10,
    order: null,
};

function Table({
    filters: tableFilters,
    columns,
    data: { isLoad, rows, count },
    asyncCall,
    mergeData,
    sortRuleChanger,
    onhandleClickLoadMore,
    customNoRowsText,
}) {
    const [{ page, sort, order }, setFilters] = useState(initialFilters);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isLoadingMoreResults, setLoadingMoreResults] = useState(false);

    useEffect(() => {
        if (
            tableFilters?.order &&
            tableFilters?.page &&
            tableFilters.page &&
            tableFilters.order
        ) {
            setFilters((prevState) => ({
                ...prevState,
                ...tableFilters,
            }));
            if (asyncCall) asyncCall({ ...tableFilters }, tableFilters.page === 1);
        }
    }, [tableFilters]);

    useEffect(() => {
        setLoading(isLoad);
    }, [isLoad]);

    useEffect(() => {
        if (mergeData && page > 1 && rows.length) {
            setTableData((prevState) => [...prevState, ...rows]);
        } else {
            setTableData(rows);
        }
        if (isLoadingMoreResults) {
            setLoadingMoreResults(false);
        }
        if (isLoading) {
            setLoading(false);
        }
    }, [rows]);

    const memoizedTableData = useMemo(() => tableData, [tableData]);

    return (
        <div className={`${styles.Table}`}>
            <TableHeader
                rows={memoizedTableData}
                sort={sort}
                order={order}
                onSort={sortRuleChanger}
                columns={columns}
            />
            <div className={`${styles.TableContent}`}>
                {isLoading && (
                    <div className={`${styles.TableLoadingdata}`}>
                        <span>
                            <i className="fas fa-circle-notch fa-spin" /> Loading Data...
                        </span>
                    </div>
                )}
                <TableRow
                    isLoading={isLoading}
                    columns={columns}
                    rows={memoizedTableData}
                    customNoRowsText={customNoRowsText}
                    order={order}
                    total={count}
                />
            </div>
            {!isLoading && memoizedTableData.length < count && (
                <span
                    onClick={() => {
                        setLoadingMoreResults(true);
                        onhandleClickLoadMore();
                    }}
                    className={`${styles.TableLoadMore} link`}
                >
                    {isLoadingMoreResults ? (
                        <span>
                            <i className="fas fa-circle-notch fa-spin" /> Loading...
                        </span>
                    ) : (
                        <span>Load more</span>
                    )}
                </span>
            )}
        </div>
    );
}

Table.defaultProps = {
    columns: [],
    filters: {
        page: 1,
        limit: 10,
        order: null,
        sort: 'desc',
    },
    data: {
        isLoad: false,
        rows: [],
        count: 0,
    },
    asyncCall: null,
    mergeData: false,
    sortRuleChanger: () => {},
    onhandleClickLoadMore: () => {},
    customNoRowsText: null,
};

Table.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape),
    filters: PropTypes.objectOf({
        page: PropTypes.number,
        limit: PropTypes.number,
        order: PropTypes.string,
        sort: PropTypes.string,
    }),
    data: PropTypes.objectOf({
        isLoad: PropTypes.bool,
        rows: PropTypes.arrayOf(PropTypes.shape),
        count: PropTypes.number,
    }),
    asyncCall: PropTypes.func || PropTypes.number,
    mergeData: PropTypes.bool,
    sortRuleChanger: PropTypes.func,
    onhandleClickLoadMore: PropTypes.func,
    customNoRowsText: PropTypes.string || PropTypes.number,
};

export default Table;
